<script lang="ts">
import { LS_POPUPS, LS_SINGLE_POPUP, PopupMode } from "@/app/const";
import axios from "@/app/http/axios";
import { v4 as uuidv4 } from "uuid";
import LocalStorage from "@/app/localStorage";
import { FileUpload } from "@/components/layouts/types";
import Popup from '@/interface/Popup.interface';
import { ref, stop } from "vue";

export default {
    setup() {
        const popupsLS = new LocalStorage(LS_POPUPS);
        const popupSingleLS = new LocalStorage(LS_SINGLE_POPUP);
        const slideIndex = ref(0);
        return { popupsLS, slideIndex, popupSingleLS };
    },
    data: ($this) => ({
        allPopups: [] as any[],
        popupsDisplay: [] as any[],
        mode: PopupMode.SINGLE,
        singlePopupDisplay: false,
        popupMode: PopupMode,
        progressInterval: undefined as any,
        hover: false,
        count: 1,
    }),
    computed: {
    },
    async mounted() {
        if (this.mode === PopupMode.MULTIPLE) {
            const _popups = this.popupsLS.getItems();
            if (JSON.stringify(_popups) == '{}') {
                await this.getPopups();
            } else {
                await this.getPopupsNotClosed();
            }
        } else if (this.mode === PopupMode.SINGLE) {
            const closed = this.popupSingleLS.getItems();
            if (JSON.stringify(closed) == '{}' || !closed) {
                await this.getPopups();
            }
            // set interval for auto next image
            this.countToHundred();
        }
    },
    unmounted() {
        clearInterval(this.progressInterval);
    },
    methods: {
        async mapImageToPopup() {
            for (let i = 0; i < this.popupsDisplay.length; i++) {
                let img: FileUpload = {
                    ...this.popupsDisplay[i].image,
                    src: this.popupsDisplay[i].image && this.popupsDisplay[i].image.src ? this.popupsDisplay[i].image.src : this.popupsDisplay[i].image,
                    name: (this.popupsDisplay[i].image && this.popupsDisplay[i].image.name) || `uploaded-img-` + i + ".png",
                    size: (this.popupsDisplay[i].image && this.popupsDisplay[i].image.size) || 1024,
                    id: (this.popupsDisplay[i].image && this.popupsDisplay[i].image.id) || uuidv4(),
                };
                if (this.popupsDisplay[i].image) {
                    const base64Response = await fetch(this.popupsDisplay[i].image.blob);
                    const blob = await base64Response.blob();
                    this.popupsDisplay[i].image.src = URL.createObjectURL(blob);
                }
                this.popupsDisplay[i].image = img;
                this.popupsDisplay[i].display = true;
            }

            if (this.mode === PopupMode.SINGLE) {
                this.singlePopupDisplay = true;
            }
        },
        async getPopups() {
            const path = 'api/popup';
            try {
                const res = await axios.get<Popup[]>(`${path}`);
                this.allPopups = res.data;
                const ls = this.allPopups.map(o => ({ id: o.id, closed: false }));
                this.popupsLS.setItems(ls);
                this.popupsDisplay = [...this.allPopups];
                await this.mapImageToPopup();
            } catch (error: any) {
                throw error;
            }
        },
        async getPopupsNotClosed() {
            const path = 'api/popup';
            try {
                const res = await axios.get<Popup[]>(`${path}`);
                this.allPopups = res.data;
                // add popup that just added from admin page to local storage
                const newPopupsLS = this.popupsLS.getItems();
                const popupIdsFromLS = newPopupsLS.map((o: any) => o.id);
                let popupNotExistInLS = this.allPopups.filter(o => !popupIdsFromLS.includes(o.id));
                popupNotExistInLS.forEach(p => {
                    newPopupsLS.push({ id: p.id, closed: false });
                })
                this.popupsLS.setItems(newPopupsLS);
                // filtered only not closed popup
                const idsNotClosed = newPopupsLS.filter((o: any) => !o.closed)?.map((m: any) => m.id) as any[];
                // set popupsDisplayed to only display popups that never closed yet
                this.popupsDisplay = [...res.data?.filter(o => idsNotClosed.includes(o.id))];
                await this.mapImageToPopup();
            } catch (error: any) {
                throw error;
            }
        },
        async close(popup?: any) {
            if (popup) {
                popup.display = false;
                const _popups = this.popupsLS.getItems();
                let p = _popups.find((o: any) => o.id === popup.id)
                p.closed = true;
                this.popupsLS.setItems(_popups);
            } else {
                this.popupSingleLS.setItems(true);
                this.singlePopupDisplay = false;
            }
        },
        onMouseOver() {
            this.hover = true;
        },
        onMouseLeave() {
            this.hover = false;
        },
        countToHundred() {
            this.count = 1;
            const interval = 40; // milliseconds
            const totalCount = 100;

            this.progressInterval = setInterval(() => {
                if (!this.hover) {
                    this.count++;
                }
                if (this.count > totalCount) {
                    clearInterval(this.progressInterval);
                    if (this.slideIndex < this.popupsDisplay.length) {
                        this.slideIndex++;
                        if (this.slideIndex === this.popupsDisplay.length) {
                            this.slideIndex = 0;
                        }
                    }
                    this.countToHundred();
                }
            }, interval);
        }
    },
};
</script>
<template>
    <v-dialog v-if="mode === popupMode.MULTIPLE" v-for="(popup, i) in popupsDisplay" v-model="popup.display"
        :close-on-back="false" max-width="80vw" persistent>
        <v-card height="60vh">
            <v-toolbar>
                <v-toolbar-title>{{ popup.subject }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                    <v-btn icon="mdi-close" @click="close(popup)"></v-btn>
                </v-toolbar-items>
            </v-toolbar>
            <v-img height="80%" :src="popup.image.src" cover></v-img>
            <v-card-actions class="d-flex align-end justify-center mt-4 mb-7">
                <v-btn color="light" @click="close(popup)" class="me-2" flat variant="elevated">
                    {{ $t('t-close') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    <v-dialog v-if="mode === popupMode.SINGLE" v-model="singlePopupDisplay" max-width="80vw" persistent>
        <v-card height="60vh" class="">
            <v-toolbar>
                <v-toolbar-title>ข่าวประกาศทั้งหมด</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                    <v-btn icon="mdi-close" @click="close()"></v-btn>
                </v-toolbar-items>
            </v-toolbar>
            <v-carousel hide-delimiters v-model="slideIndex" height="100%" :width="400"
                :show-arrows="popupsDisplay.length > 1" class="pt-5 pb-3">
                <v-carousel-item v-for="(item, i) in popupsDisplay" :key="i" :src="item.image.src"
                    @mouseover="onMouseOver()" @mouseleave="onMouseLeave()">
                </v-carousel-item>
            </v-carousel>
            <div class="text-center text-h6 mb-2">{{ slideIndex + 1 }}/{{ popupsDisplay.length }}</div>
            <v-progress-linear color="primary" height="6" :model-value="count" rounded></v-progress-linear>
        </v-card>
    </v-dialog>
</template>
